/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable no-underscore-dangle */
/* eslint-disable max-statements */
import { isPlatformWeb } from 'renative';
import autoBind from 'auto-bind';

import { allSettled, firstLetterUppercase, groupWith } from '@24i/nxg-core-utils';
import { BaseApiParams, HTTPQuery } from '@24i/nxg-core-utils/src/api';
import { log } from '@24i/nxg-core-utils/src/logger';
import {
    Asset,
    AssetGuard,
    AssetImages,
    ASSET_TYPE,
    Broadcast,
    Channel,
    ChannelGuard,
    ContentDataClient,
    Edition,
    Episode,
    Image,
    LiveEvent,
    PageSection,
    Season,
    Stream,
    StreamGuard,
    FetchAssetsResponse,
    SearchAssetsByTypeResponse,
    PrepareStreamResponse,
    BLOCKING_REASON_TYPES,
    NEWS_SERIES_BACKSTAGE_LABEL,
    PrepareStreamOptions,
    FetchStreamOptions,
} from '@24i/nxg-sdk-photon';
import { TextPageContent } from '@24i/nxg-sdk-photon/src/models/textPageContent';
import {
    EpgDataClientStub,
    STUB_EPG_SEPARATOR,
} from '@24i/nxg-sdk-smartott-stubs/src/clients/EpgDataClient';

import { BackstageApiBase } from '../../base';
import {
    EditionsResponseGuard,
    ItemsResponse,
    ItemsResponseGuard,
    PaginatedResponse,
    PaginatedResponseGuard,
    BackstageAssetGuard,
    BackstageBroadcastGuard,
    BackstagePlaylistGuard,
    BackstagePlaylist,
    BackstageAsset,
    BackstageEpisode,
    BackstageEpisodeGuard,
    BackstageBroadcast,
} from './guards';
import { mapEditionToAssetTrailer, mapSearchAssetsToSections } from './helpers/mapSource';
import { getLiveAssetTimeInformation, replaceEmptyArraysWithEmptyObjects } from '../../utils';
import { mapProgramsResponse } from './mappers';
import {
    determineAgeClassification,
    extractUniquePlaylistAssets,
    formatComputedPlaylistAssetKey,
    formatPlaylistAssetKey,
} from './utils';

export const determineEndpointType = <T extends { type: ASSET_TYPE | null }>(asset: T) => {
    let endpointType: string;

    switch (asset.type) {
        case ASSET_TYPE.EPISODE:
            endpointType = 'episodes';
            break;
        case ASSET_TYPE.LIVE_EVENT:
        case ASSET_TYPE.EPG:
            endpointType = 'live-events';
            break;
        case ASSET_TYPE.MOVIE:
            endpointType = 'movies';
            break;
        case ASSET_TYPE.NEWS_SERIES:
            endpointType = NEWS_SERIES_BACKSTAGE_LABEL;
            break;
        case ASSET_TYPE.SERIES:
            endpointType = 'series';
            break;
        case ASSET_TYPE.CHANNEL:
            endpointType = 'channels';
            break;
        case ASSET_TYPE.BROADCAST:
            endpointType = 'broadcasts';
            break;
        case ASSET_TYPE.CLIP:
            endpointType = 'clips';
            break;
        case ASSET_TYPE.PODCAST_SERIES:
            endpointType = 'podcasts';
            break;
        default:
            throw new Error(`invalid or unsupported asset type ${asset.type}`);
    }

    return endpointType;
};

export class SharedContentDataClient
    extends BackstageApiBase
    implements Partial<ContentDataClient>
{
    stubEpgClient: EpgDataClientStub;

    channelsCacheMap: Map<string | number, Channel>;

    constructor(params: BaseApiParams) {
        super(params);
        autoBind(this);
        this.stubEpgClient = new EpgDataClientStub();
        this.channelsCacheMap = new Map();
    }

    async fetchEditions(asset: Asset) {
        const editionType = determineEndpointType({ type: asset.type });
        // Makes no sense to ask for editions on series
        if (['series', 'podcasts', 'broadcasts'].includes(editionType)) {
            return [];
        }

        const id = asset.type === ASSET_TYPE.EPG && asset.isLive ? asset?.channel?.id : asset.id;

        return this.request({
            path: `/media/${editionType}/${id}/editions`,
            method: 'GET',
            guard: EditionsResponseGuard,
        });
    }

    async fetchStream(asset: Asset, options: FetchStreamOptions): Promise<Stream> {
        const assetType = determineEndpointType({ type: asset.type });
        const query: HTTPQuery = {};
        Object.keys(options).forEach((key) => {
            const value = options[key];
            if (value) {
                query[key] = value;
            }
        });
        return this.request<Stream>({
            path: `/media/${assetType}/${asset.id}/stream`,
            method: 'GET',
            guard: StreamGuard,
            query,
        }).then(replaceEmptyArraysWithEmptyObjects<Stream>(['data']));
    }

    async prepareStream(
        inputAsset: Asset,
        options: PrepareStreamOptions = {}
    ): Promise<PrepareStreamResponse> {
        let asset = inputAsset;
        try {
            let isStubChannel = false;
            if (asset.id?.includes(STUB_EPG_SEPARATOR)) {
                const broadcast = await this.stubEpgClient.getBroadcastById({
                    broadcastId: asset.id,
                    channelId: asset.channelId,
                });

                if (!broadcast) throw new Error('Broadcast not found');

                isStubChannel = true;
            }
            if (asset.type === ASSET_TYPE.CHANNEL) {
                const channels = await this.stubEpgClient.getChannels();
                if (channels.find((channel) => channel.id === asset.id)) {
                    isStubChannel = true;
                }
            }

            if (isStubChannel) {
                const url =
                    asset.isLive || asset.type === ASSET_TYPE.CHANNEL
                        ? `https://cph-p2p-msl.akamaized.net/hls/live/2000341/test/master.m3u8#${asset.id}`
                        : 'https://bitdash-a.akamaihd.net/content/MI201109210084_1/m3u8s/f08e80da-bf1d-4e3d-8899-f0f6155f6efa.m3u8';
                const stream: Stream = {
                    type: 'hls',
                    drm: 'unencrypted',
                    url,
                };
                return {
                    edition: {
                        editionType: 'full',
                        id: asset.id,
                        label: asset.title || 'Full stream',
                        subtitles: [],
                        streams: [stream],
                    },
                    stream,
                    allStreams: [stream],
                };
            }

            const isStartOver = asset.isStartOver || false;
            // Hack until BS resolves error on editions/ endpoint for live channels
            if (
                asset.type === ASSET_TYPE.BROADCAST &&
                asset.isLive &&
                // !startOver &&
                asset.channel
            ) {
                return await this.prepareStream(
                    {
                        ...asset.channel,
                        type: ASSET_TYPE.CHANNEL,
                        id: asset.channelId || asset.channel.id,
                        isStartOver,
                    },
                    options
                );
            }
            // End of hack

            // If we request a series, fetchEditions will fail
            if (asset.type === ASSET_TYPE.SERIES && !asset.isTrailer) {
                const seasons = await this.fetchEpisodes(asset.id);
                const seasonsWithEpisodes = seasons
                    .filter((s) => s?.episodes?.length)
                    .sort((a, b) => (a?.seasonNumber ?? 0) - (b?.seasonNumber ?? 0));
                if (seasonsWithEpisodes?.length) {
                    const [firstEpisode] = seasonsWithEpisodes[0].episodes;
                    asset = firstEpisode;
                }
            }

            const { includeAllStreams, streamSelector } = options;

            const editions = await this.fetchEditions(asset);
            if (!editions) throw new Error('USER_NOT_AUTHENTICATED');
            if (!editions.length) throw new Error('No editions available for this item');

            const selectedEditionType = asset.isTrailer ? 'trailer' : 'full';
            const selectedEdition = editions.find((ed) =>
                asset.editionId ? ed.id === asset.editionId : ed.editionType === selectedEditionType
            );

            const edition = selectedEdition || editions[0];
            const editionId = asset.editionId || edition.id;

            // Check if edition is blocked
            if (edition.blocked) {
                const firstReason = edition.blocked[0];
                if (firstReason?.reason === BLOCKING_REASON_TYPES.PRODUCTS_MISMATCH) {
                    throw new Error(firstReason?.reason);
                }
            }

            let streamsToLoad = edition.streams;
            if (streamSelector) {
                streamsToLoad = streamsToLoad
                    .map((s) => ({
                        stream: s,
                        score: streamSelector(s.type, s.drm),
                    }))
                    .filter((a) => a.score > 0)
                    .sort((a, b) => b.score - a.score)
                    .map((a) => a.stream);
            }
            if (!includeAllStreams && streamsToLoad.length > 1) {
                streamsToLoad = [streamsToLoad[0]];
            }

            // Prefilter failing manifests
            const streamPromises = streamsToLoad.map((s) =>
                this.fetchStream(asset, {
                    editionId,
                    type: s.type,
                    drm: s.drm,
                    startOver: isStartOver,
                })
            );

            const streamResults = await allSettled(streamPromises);
            const finalStreams = streamResults.flatMap((s) =>
                s.status === 'fulfilled' ? [s.value] : []
            );
            return {
                edition,
                stream: finalStreams[0],
                allStreams: includeAllStreams ? finalStreams : undefined,
            };
        } catch (err) {
            log('ERROR PREPARING STREAM', err);
            throw err;
        }
    }

    async fetchEPGChannelCurrentLiveProgramme(id: string): Promise<PaginatedResponse<Broadcast>> {
        const dateNow = Date.now();
        const dateFiveHoursFromNow = dateNow + 5 * 3600 * 1000;

        return this.request({
            path: `/media/channels/${id}/epg`,
            method: 'GET',
            guard: PaginatedResponseGuard(BackstageBroadcastGuard),
            query: {
                from: Math.floor(dateNow / 1000),
                to: Math.floor(dateFiveHoursFromNow / 1000),
            },
        })
            .then(mapProgramsResponse)
            .catch((err) => {
                log('ERROR FETCHING CHANNEL PROGRAM DATA', err);

                throw err;
            });
    }

    async fetchChannel(id: string | number): Promise<Channel> {
        if (this.channelsCacheMap.has(id)) {
            return this.channelsCacheMap.get(id) as Channel;
        }

        const response = await this.request({
            path: `/media/channels/${id}`,
            method: 'GET',
            guard: ChannelGuard,
        }).catch((err) => {
            log('ERROR FETCHING CHANNEL', err);

            throw err;
        });

        this.channelsCacheMap.set(id, response);
        return response;
    }

    async searchAssetsByType(
        query: string,
        translation?: (label: string, { count }: { count: number }) => string,
        offset = 0,
        size = 500
    ): Promise<SearchAssetsByTypeResponse> {
        const response = await this.request({
            path: `/search`,
            method: 'GET',
            guard: BackstagePlaylistGuard,
            query: {
                q: query,
                offset,
                size,
            },
        });

        const transformedAssets = await this.transformResponse({ items: response.items });
        const searchedItems = transformedAssets.map((item) => ({ ...item, progress: 0 }));
        return { ...response, items: mapSearchAssetsToSections(searchedItems, translation) };
    }

    async searchPaginatedAssets(
        query: string,
        offset = 0,
        size = 24
    ): Promise<FetchAssetsResponse> {
        const response = await this.request({
            path: `/search`,
            method: 'GET',
            guard: BackstagePlaylistGuard,
            query: {
                q: query,
                offset,
                size,
            },
        });

        const transformedItems = await this.transformResponse({ items: response.items });
        const searchedItems = transformedItems.map((item) => ({ ...item, progress: 0 }));

        return { ...response, items: searchedItems };
    }

    async searchAutoSuggest(query: string): Promise<string[]> {
        return this.request({
            path: `/search/autosuggest`,
            method: 'GET',
            query: {
                q: query,
            },
        });
    }

    async fetchAssets(
        playlistId: PageSection['playlistId'],
        sorting?: PageSection['sorting'],
        pinnedItems?: PageSection['pinnedItems'],
        offset?: number,
        size?: number
    ): Promise<FetchAssetsResponse> {
        return this.request({
            path: `/playlists/${playlistId}`,
            method: 'GET',
            guard: BackstagePlaylistGuard,
            query: {
                ...(offset ? { offset } : { offset: 0 }),
                ...(size ? { size } : {}),
                ...(sorting?.field ? { sortField: sorting?.field } : {}),
                ...(sorting?.order ? { sortOrder: sorting?.order } : {}),
                ...(pinnedItems?.length ? { pinnedItems: pinnedItems.join(',') } : {}),
            },
        })
            .then(async (response) => {
                const items = await this.transformResponse(response);
                return {
                    total: response.total,
                    offset: response.offset,
                    items,
                };
            })
            .catch((err) => {
                log('ERROR FETCHING ASSETS', err);

                throw err;
            });
    }

    async fetchEpisodesList(
        id: string,
        mediaType: ASSET_TYPE
    ): Promise<ItemsResponse<BackstageEpisode>> {
        const mediaTypeToUse = mediaType ?? ASSET_TYPE.SERIES;

        try {
            const episodes = await this.request({
                path: `/media/${mediaTypeToUse}/${id}/episodes`,
                method: 'GET',
                guard: ItemsResponseGuard(BackstageEpisodeGuard),
                query: {
                    size: 300,
                },
            });

            return episodes;
        } catch (err) {
            log('ERROR FETCHING EPISODES', err, `/media/${mediaTypeToUse}/${id}/episodes?size=300`);

            throw err;
        }
    }

    async fetchEpisodes(id?: string, translation?: any, mediaType?: ASSET_TYPE): Promise<Season[]> {
        if (!id) {
            return [];
        }
        const mediaTypeToUse = mediaType ?? ASSET_TYPE.SERIES;
        const seriesEpisodes = await this.fetchEpisodesList(id, mediaTypeToUse);
        const groupedEpisodes = groupWith(
            seriesEpisodes.items,
            (episode) => episode.seasonNumber ?? 0
        );
        return this.transformSeasons(groupedEpisodes, translation);
    }

    async fetchLatestNewsEpisode(newsSeriesId: string): Promise<Episode | null> {
        const newsSeriesEpisodes = await this.fetchEpisodesList(
            newsSeriesId,
            ASSET_TYPE.NEWS_SERIES
        );

        const [latestEpisode] = [...newsSeriesEpisodes.items].sort(
            (a: BackstageEpisode, b: BackstageEpisode) =>
                (a.releaseDateTime ?? 0) < (b.releaseDateTime ?? 0) ? 1 : -1
        );

        if (!latestEpisode) return null;

        return {
            ...this.transformOpts(latestEpisode),
            series: newsSeriesId,
            seriesType: ASSET_TYPE.NEWS_SERIES,
        } as Episode;
    }

    async checkBlockers(asset: Asset): Promise<Asset['blocked']> {
        const isLiveBroadcast = asset.type === ASSET_TYPE.BROADCAST && asset.isLive;
        const assetType = isLiveBroadcast ? 'channels' : determineEndpointType(asset);
        const id = isLiveBroadcast ? asset.channelId : asset.id;

        return this.request({
            path: `/media/${assetType}/${id}`,
            method: 'GET',
            query: {
                size: 300,
            },
            guard: AssetGuard,
        })
            .then((r: Asset) => r.blocked)
            .catch((err) => {
                log('ERROR FETCHING ASSET BLOCKERS INFORMATION', err, `/media/${assetType}/${id}`);

                if ([ASSET_TYPE.EPG, ASSET_TYPE.BROADCAST, ASSET_TYPE.CHANNEL].includes(asset.type))
                    return undefined;
                throw err;
            });
    }

    async fetchAsset(asset: Asset): Promise<Asset> {
        if (asset.id.includes(STUB_EPG_SEPARATOR)) {
            return this.stubEpgClient.getBroadcastById({
                broadcastId: asset.id,
                channelId: asset.channelId,
            }) as unknown as Asset;
        }

        const assetType = determineEndpointType(asset);

        try {
            const response = await this.request({
                path: `/media/${assetType}/${asset.id}`,
                method: 'GET',
                guard: BackstageAssetGuard,
                query: {
                    ...(asset.type === ASSET_TYPE.SERIES ? { includeEpisodes: false } : {}),
                },
            });

            return await this.transformAsset(response);
        } catch (err) {
            log('ERROR FETCHING ASSET', err, `/media/${assetType}/${asset.id}`);

            throw err;
        }
    }

    async fetchRecommended(assetId?: string, type?: ASSET_TYPE, size?: number): Promise<Asset[]> {
        if (!assetId || !type) {
            return [];
        }
        const assetType = determineEndpointType({ type });

        return this.request({
            path: `/media/${assetType}/${assetId}/recommendations`,
            method: 'GET',
            guard: BackstagePlaylistGuard,
            query: { ...(size && { size }) },
        })
            .then(this.transformResponse)
            .catch((err) => {
                log(
                    'ERROR FETCHING RECOMMENDED',
                    err,
                    `/media/${assetType}/${assetId}/recommendations`
                );

                return [];
            });
    }

    // Preload channel data, need to get this data inside deeper function calls
    async preloadChannelData(channelIds: string[]) {
        return Promise.all(channelIds.map((v) => this.fetchChannel(v)));
    }

    // Fetch playlist assets
    async fetchPlaylistAssets(playlistAssets: string[]): Promise<[string, Asset[]][]> {
        return Promise.all(
            playlistAssets.map(async (v) => {
                try {
                    const data = JSON.parse(v);
                    return [
                        v,
                        (await this.fetchAssets(data.playlistId, data.sorting, data.pinnedItems))
                            ?.items,
                    ];
                } catch (e) {
                    return [v, []];
                }
            })
        );
    }

    // Fetch computed playlist and preload channel data (need to get access in another place and avoid to load multiple times)
    async fetchAndPreloadComputedPlaylistAssets(
        playlistAssets: string[]
    ): Promise<[string, Asset[]][]> {
        try {
            const uniqLiveEventChannelId = new Set();
            const result = await Promise.all(
                playlistAssets.map(async (v) => {
                    try {
                        const playlist = await this.fetchComputedPlaylistRequest(v);
                        playlist.items?.forEach((item) => {
                            if ([ASSET_TYPE.BROADCAST, ASSET_TYPE.LIVE_EVENT].includes(item.type)) {
                                uniqLiveEventChannelId.add(item.channelId);
                            }
                        });
                        return [v, playlist];
                    } catch (e) {
                        return [v, []];
                    }
                })
            );

            this.channelsCacheMap.clear(); // Clean channels cache data before fetch new data
            await this.preloadChannelData(Array.from(uniqLiveEventChannelId) as string[]); // preload channels data

            return result as [string, Asset[]][];
        } catch (e) {
            return [];
        }
    }

    async fetchPlaylist(
        response: PageSection[],
        fetchSize = Infinity,
        pageNumber = 0
    ): Promise<PageSection[]> {
        if (!response?.length) {
            return [];
        }

        const fetchingFrom = fetchSize * pageNumber;
        const fetchingTo = Math.min(response.length, fetchSize * (pageNumber + 1));
        const responsePart = response.slice(fetchingFrom, fetchingTo);

        const { uniqPlaylistAssets, uniqComputedPlaylistAssets } =
            extractUniquePlaylistAssets(responsePart);

        const assetsPlaylistMap: Map<string, Asset[]> = new Map(
            await this.fetchPlaylistAssets(uniqPlaylistAssets)
        );
        const assetsComputedPlaylistMap: Map<string, Asset[]> = new Map(
            await this.fetchAndPreloadComputedPlaylistAssets(uniqComputedPlaylistAssets)
        );

        const items = response.slice(fetchingFrom, fetchingTo).map(async (page: PageSection) => {
            let assets: Asset[] = [];
            try {
                if (page.playlistId) {
                    assets = assetsPlaylistMap.get(formatPlaylistAssetKey(page)) || [];
                } else if (page.computedPlaylist && page?._links?.href) {
                    const key = formatComputedPlaylistAssetKey(page);
                    if (key) {
                        const computedPlaylist = assetsComputedPlaylistMap.get(key);
                        assets = computedPlaylist
                            ? await this.transformResponse(
                                  computedPlaylist as Partial<BackstagePlaylist>
                              )
                            : [];
                    }
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.warn('Error fetching assets for playlist', page);
            }

            return { ...page, items: assets };
        });

        this.channelsCacheMap.clear(); // Clean channels cache data in the end - this data not need
        return Promise.all(items);
    }

    async fetchFavoritesPlaylist({
        offset,
        size,
    }: {
        offset?: number;
        size?: number;
    }): Promise<Asset[]> {
        return this.request({
            path: `/playlists/user-favorites`,
            method: 'GET',
            guard: BackstagePlaylistGuard,
            query: {
                ...(offset ? { offset } : {}),
                ...(size ? { size } : {}),
            },
        }).then(this.transformResponse);
    }

    async fetchContinueWatchingPlaylist(
        offset?: number,
        size?: number,
        signal?: AbortSignal
    ): Promise<Asset[]> {
        return this.request({
            path: `/playlists/user-continue-watching`,
            method: 'GET',
            guard: BackstagePlaylistGuard,
            query: {
                ...(offset ? { offset } : {}),
                ...(size ? { size } : {}),
            },
            signal,
        }).then(this.transformResponse);
    }

    async fetchWatchHistoryPlaylist(offset?: number, size?: number): Promise<Asset[]> {
        return this.request({
            path: `/playlists/watch-history`,
            method: 'GET',
            guard: BackstagePlaylistGuard,
            query: {
                ...(offset ? { offset } : {}),
                ...(size ? { size } : {}),
            },
        }).then(this.transformResponse);
    }

    async fetchClips(clipIds: string[]): Promise<Asset[]> {
        return Promise.all(
            clipIds.map((clip) => this.fetchAsset({ id: clip, type: ASSET_TYPE.CLIP }))
        );
    }

    async fetchExtras(asset: Asset): Promise<Asset[]> {
        let extras: Asset[] = [];
        try {
            // Extras are composed of trailers of current asset and first editions from clips from current asset.
            const [fetchedClips = [], fetchedAssetEditions = []] = await Promise.all([
                this.fetchClips(asset.clipIds || []),
                this.fetchEditions({
                    id: asset.id,
                    type: asset.type,
                }),
            ]);

            const fetchedClipsEditions: Array<Edition[]> = await Promise.all(
                fetchedClips.map((clip) =>
                    this.fetchEditions({ id: clip.id, type: ASSET_TYPE.CLIP })
                )
            );

            const trailerFromAsset: Asset[] = fetchedAssetEditions
                .filter((edition: Edition) => edition.editionType === 'trailer')
                .map((edition) => mapEditionToAssetTrailer(edition, asset));

            const extrasFromClips: Asset[] = fetchedClipsEditions
                .map((editions, clipIndex) => {
                    // When getting editions from clips, only first one from array will be used.
                    const defaultEdition = editions.find(
                        (edition) => edition.editionType === 'full'
                    );
                    return {
                        ...fetchedClips[clipIndex],
                        editionId: defaultEdition?.id,
                        isTrailer: defaultEdition?.editionType === 'trailer',
                    };
                })
                .filter((extraClip) => extraClip !== undefined);

            extras = trailerFromAsset.concat(extrasFromClips.filter((extra) => !!extra.editionId));
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('Error fetching extras for asset', e);
        }
        return extras;
    }

    getImagesForAsset = (
        asset: BackstageAsset | Asset
    ): { [key in keyof AssetImages]: Image['url'] | undefined | null } => {
        const getImageUrl = (
            imageKey: keyof AssetImages,
            fallbackIndex?: number
        ): string | undefined | null => {
            const images = asset.images?.[imageKey];
            if (images && images.length > 0) {
                // TODO: Implement logic to choose the best image from the array
                // For now, return the last image if available, otherwise, return the image at the fallback index or the first image
                if (fallbackIndex !== undefined) {
                    return images[fallbackIndex]?.url || images[0]?.url;
                }
                const lastIndex = images.length - 1;
                return images[lastIndex]?.url || images[0]?.url;
            }
            return undefined;
        };

        const poster = getImageUrl('poster', 2);
        const still = getImageUrl('still', 2);
        const square = getImageUrl('square', 2);
        const highlight = getImageUrl('highlight', 2);
        const background = getImageUrl('background');
        const backgroundPortrait = getImageUrl('backgroundPortrait');
        const heroLandscape = getImageUrl('heroLandscape', isPlatformWeb ? 2 : 0);
        const heroPortrait = getImageUrl('heroPortrait', 2);
        const packshotLandscape = getImageUrl('still', 0);

        return {
            poster,
            still,
            square,
            background,
            heroLandscape,
            heroPortrait,
            backgroundPortrait,
            highlight,
            packshotLandscape,
        };
    };

    protected transformOpts(asset: BackstageAsset): Asset {
        const {
            poster,
            still,
            square,
            background,
            heroLandscape,
            heroPortrait,
            backgroundPortrait,
            highlight,
            packshotLandscape,
        } = this.getImagesForAsset(asset);
        let subtitle: string | undefined | null = asset.genres?.[0]?.label;
        let channelLogo: string | undefined | null;
        let number: number | undefined | null;
        let duration: number | undefined | null = asset?.duration;
        let rating = 0;
        const ageClassification = determineAgeClassification(asset);

        const ratingNumber = Number(asset.rating);

        if (ratingNumber) {
            /*
             * Backstage returns a value between 0-100,
             * we want a value between 0-5 (stars) (rounded to 1 decimal)
             */
            rating = Math.round(((ratingNumber * 5) / 100) * 10) / 10;
        }

        const crew = (asset.crew || []).map((c) => ({ ...c, name: c.label }));

        const releaseDate = asset.releaseDate?.slice(0, 4) || asset.year?.toString() || '';
        const fullReleaseDate = asset.releaseDate ?? null;

        if (asset.genres?.[1]?.label) {
            subtitle += `, ${asset.genres[1].label}`;
        }

        if (asset.type === ASSET_TYPE.CHANNEL) {
            number = asset.number;
            subtitle = undefined;
            if (asset.images?.logo) {
                channelLogo =
                    asset.images?.logo[4]?.url ||
                    asset.images?.logo[3]?.url ||
                    asset.images?.logo[2]?.url ||
                    asset.images?.logo[1]?.url ||
                    asset.images?.logo[0]?.url;
            }
        }

        if (asset.type === ASSET_TYPE.BROADCAST || asset.type === ASSET_TYPE.LIVE_EVENT) {
            if (asset.endsAt && asset.startsAt) {
                duration = asset.endsAt - asset.startsAt;
            }
        }

        return {
            channelLogo,
            number,
            description: asset.description,
            poster,
            still,
            square,
            background,
            heroLandscape,
            heroPortrait,
            backgroundPortrait,
            highlight,
            packshotLandscape,
            genres: asset.genres,
            id: asset.id,
            subtitle,
            title: asset.label,
            rating,
            releaseDate,
            clipIds: asset.clipIds || [],
            type: asset.type,
            crew,
            duration,
            startsAt: asset.startsAt,
            endsAt: asset.endsAt,
            continueWatchingOffset: asset.continueWatchingOffset ?? null,
            continueWatchingLastTime: asset.continueWatchingLastTime || 0,
            ageClassification,
            externalId: asset.external_id,
            blocked: asset.blocked,
            assetLabel: asset.assetLabel,
            isAdult: asset.isAdult,
            externalAuthDetails: asset.externalAuthDetails,
            fullReleaseDate,
            releaseDateTime: asset.releaseDateTime,
            attributionId: asset.attributionId,
        };
    }

    protected async transformAsset(asset: BackstageAsset): Promise<Asset> {
        const transformedOpts = this.transformOpts(asset);
        let model: Asset | Episode;
        switch (asset.type) {
            case ASSET_TYPE.EPISODE: {
                const episode = asset as Episode;
                const {
                    seasonNumber,
                    episodeNumber,
                    series,
                    nextEpisodeId,
                    podcastName,
                    seriesType,
                    seriesName,
                } = episode;
                // todo For now i just comment this piece of code. For now i don't see
                // why we need fetch for each serie title, becase we have info in episode
                // let seriesTitle;
                //
                // if (series && seriesType === ASSET_TYPE.SERIES) {
                //     try {
                //         const serie = await this.fetchAsset({
                //             id: series,
                //             type: ASSET_TYPE.SERIES,
                //         });
                //
                //         seriesTitle = serie.title;
                //     } catch (err) {
                //         // eslint-disable-next-line no-console
                //         console.warn('Error fetching series', series, err);
                //     }
                // }

                model = {
                    ...transformedOpts,
                    seasonNumber,
                    episodeNumber,
                    series,
                    nextEpisodeId,
                    podcastName,
                    seriesType,
                    seriesName,
                };

                break;
            }

            case ASSET_TYPE.CHANNEL: {
                // Get information about currently live programme
                const liveProgram = await this.fetchEPGChannelCurrentLiveProgramme(asset.id).then(
                    (fetchedChannel) => {
                        const onGoingProgram = fetchedChannel?.items?.[0];
                        const { progress, timeLeft, isLive } =
                            getLiveAssetTimeInformation(onGoingProgram);
                        const onGoingProgramImages = onGoingProgram
                            ? this.getImagesForAsset(onGoingProgram)
                            : undefined;
                        return {
                            progress,
                            timeLeft,
                            isLive,
                            startsAt: onGoingProgram?.startsAt || onGoingProgram?.start,
                            endsAt: onGoingProgram?.endsAt || onGoingProgram?.end,
                            broadcastMetadata: {
                                ...onGoingProgram,
                                ...onGoingProgramImages,
                            },
                        };
                    }
                );

                model = {
                    ...transformedOpts,
                    ...liveProgram,
                };

                break;
            }

            case ASSET_TYPE.BROADCAST:
            case ASSET_TYPE.LIVE_EVENT: {
                let channel: Channel | undefined;
                let number: number | undefined | null;
                const liveEvent = asset as LiveEvent;

                if (liveEvent.channelId) {
                    try {
                        channel = await this.fetchChannel(liveEvent.channelId);
                        number = channel?.number;
                    } catch (err) {
                        // eslint-disable-next-line no-console
                        console.warn('Error fetching chanel', liveEvent.channelId, err);
                    }
                }

                const { progress, isLive } = getLiveAssetTimeInformation(asset);
                const isAdultChannel = channel?.isAdult ? { isAdult: true } : {};

                const enableCatchUp =
                    asset.type === ASSET_TYPE.BROADCAST
                        ? (asset as BackstageBroadcast).channelFeatures?.catchup?.enabled
                        : false;

                model = {
                    ...transformedOpts,
                    progress,
                    /* @ts-ignore */
                    channelLogo: channel?.images?.logo?.[0]?.url,
                    isLive,
                    // JS timestamp needed for AndroidTV
                    startsAt: asset.startsAt,
                    start: asset.startsAt ? asset.startsAt * 1000 : undefined,
                    endsAt: asset.endsAt,
                    end: asset.endsAt ? asset.endsAt * 1000 : undefined,
                    channel,
                    channelId: channel?.id,
                    number,
                    enableCatchUp,
                    ...isAdultChannel,
                };

                break;
            }

            default:
                model = transformedOpts;
                break;
        }

        return model;
    }

    protected async transformResponse(response: Partial<BackstagePlaylist>): Promise<Asset[]> {
        const transformWithoutError = async (asset: BackstageAsset) => {
            try {
                return await this.transformAsset(asset);
            } catch (e) {
                log('Error transforming asset', e);
                return null;
            }
        };
        const result = await (response.items instanceof Array
            ? Promise.all(response.items.map((asset) => transformWithoutError(asset)))
            : Promise.resolve([]));

        return result.filter((asset) => asset !== null) as Asset[];
    }

    protected transformSeasons(groups: BackstageEpisode[][], translation?: any): Season[] {
        return groups?.map((origEpisodes) => {
            let seasonName = '';
            let seasonNumber: number | undefined;

            const episodes = origEpisodes.map((episode: BackstageEpisode) => {
                const transformedEpisode: Episode = this.transformOpts(episode) as Episode;
                transformedEpisode.episodeNumber = episode.episodeNumber ?? 0;
                transformedEpisode.seasonNumber = episode.seasonNumber ?? 0;
                transformedEpisode.seriesId = episode.series;
                transformedEpisode.nextEpisodeId = episode.nextEpisodeId;
                transformedEpisode.seriesType = episode.seriesType;
                transformedEpisode.podcastName = episode.podcastName;

                seasonName = `${
                    translation
                        ? firstLetterUppercase(
                              translation('asset.series.season', {
                                  count: 1,
                              })
                          )
                        : 'Season'
                }${' '}${episode.seasonNumber}`;

                seasonNumber = transformedEpisode.seasonNumber;

                return transformedEpisode;
            });

            return {
                name: seasonName,
                seasonNumber,
                episodes: episodes.sort((a: Episode, b: Episode) =>
                    a.episodeNumber > b.episodeNumber ? 1 : -1
                ),
            };
        });
    }

    protected async fetchComputedPlaylistRequest(path: string): Promise<{ items?: Asset[] }> {
        if (!path) return {};

        return this.request({
            path: `/playlists/${path}`,
            method: 'GET',
            guard: BackstagePlaylistGuard,
        }).catch((err) => {
            log('ERROR FETCHING COMPUTED PLAYLIST', err);
            return [];
        }) as Promise<{ items?: Asset[] }>;
    }

    fetchTextPageContent(reference: string): Promise<TextPageContent> {
        try {
            return this.request({
                path: `/textPages/${reference}`,
                method: 'GET',
                query: {
                    includeItems: false,
                },
            });
        } catch (err) {
            log('ERROR FETCHING PAGE', err);

            throw err;
        }
    }

    async fetchGenreAssets(
        genreId: string,
        types?: string,
        offset?: number,
        size?: number
    ): Promise<Asset[]> {
        return this.request({
            path: `/genres/${genreId}/media`,
            method: 'GET',
            guard: BackstagePlaylistGuard,
            query: {
                ...(types ? { types } : {}),
                ...(offset ? { offset } : {}),
                ...(size ? { size } : {}),
            },
        }).then(this.transformResponse);
    }
}
